import { Table } from "antd"
import React, { useState, useEffect, useRef, useMemo } from "react"
import styles from "./PricingTable.module.css"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { columns } from "./Columns"
import { EditPricesForm } from "../EditPricesForm"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"

export const PricingTable = ({
  draftPrices,
  setDraftPrices,
  addNewPrice,
  setPricingType,
  isFlatRate,
  pricingType,
  expandedRowData,
  setExpandedRowData,
  form,
  onPriceInteraction
}) => {
  const { trip_prices = [] } = useSelector(modelSelector("trip"))
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const lastExpandedId = useRef(null)
  const combinedData = [...trip_prices, ...draftPrices]
  const filteredData = pricingType
    ? combinedData.filter(
      (item) => item.pricing_type === pricingType || !item.pricing_type
    )
    : combinedData

  const closeEditForm = () => {
    setExpandedRowKeys([])
    setExpandedRowData({})
  }

  useEffect(() => {
    if (draftPrices.length > 0) {
      const newestRow = draftPrices[draftPrices.length - 1]
      if (newestRow.id !== lastExpandedId.current) {
        setExpandedRowKeys([newestRow.id])
        if (newestRow.pricing_type) {
          setPricingType(newestRow.pricing_type)
        }
        lastExpandedId.current = newestRow.id
      }
    }
  }, [draftPrices, trip_prices, setPricingType])

  const filteredColumns = useMemo(() => {
    if (pricingType === "charge_per_person") {
      return columns.filter(col => col.key !== "flat_rate");
    }
    return columns;
  }, [pricingType]);

  return (
    <div className={styles.responsiveTableContainer}>
      <Table
        dataSource={filteredData}
        columns={filteredColumns}
        expandable={{
          expandedRowKeys,
          expandedRowRender: (record) => (
            <EditPricesForm
              record={record}
              key={record.id}
              expandedRowData={expandedRowData}
              isFlatRate={isFlatRate}
              closeEditForm={closeEditForm}
              setDraftPrices={setDraftPrices}
              addNewPrice={addNewPrice}
              setPricingType={setPricingType}
              form={form}
              onPriceInteraction={onPriceInteraction}
            />
          ),
          rowExpandable: () => true,
          expandedRowClassName: () => styles.expandedRow,
          expandIcon: ({ expanded, onExpand, record }) => (
            <Icon
              type="feather"
              name={expanded ? "ChevronUp" : "ChevronDown"}
              color="currentColor"
              width={20}
              height={20}
              onClick={(e) => onExpand(record, e)}
            />
          ),
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record.id] : [])
            setExpandedRowData(record)
            if (record.pricing_type) {
              setPricingType(record.pricing_type)
            }
          }
        }}
        pagination={false}
        rowHoverable={false}
        size="middle"
        className={styles.customTable}
        rowClassName={styles.customTableRow}
        rowKey="id"
      />

    </div>
  )
}

PricingTable.propTypes = {
  draftPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  setPricingType: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  pricingType: PropTypes.string,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func,
  onPriceInteraction: PropTypes.func
}
